import { useMutation } from '@tanstack/react-query';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../constants/firebaseModular';
import * as Sentry from '@sentry/browser';

interface UpdateVideoProgressPayload {
  patientId: string;
  videoId: string;
  position: number;
  duration: number;
  completed?: boolean;
}

const updateVideoProgress = async (payload: UpdateVideoProgressPayload) => {
  const { patientId, videoId, position, duration, completed } = payload;
  const videoProgressDocRef = doc(db, 'users', patientId, 'Chart', 'VIDEO_PROGRESS');

  try {
    // Build the entry as a nested map object
    const videoEntry: Record<string, unknown> = {
      videoId: videoId,
      lastPosition: position,
      duration: duration,
      lastUpdatedAt: serverTimestamp(),
    };

    // Only update completed fields if completed is true
    if (completed) {
      videoEntry.completed = true;
      videoEntry.completedAt = serverTimestamp();
    } else if (completed === false) {
      // Explicitly set to false if passed (e.g., resetting progress)
      videoEntry.completed = false;
      videoEntry.completedAt = null;
    }

    // Structure as nested object: { [videoId]: { ...entry } }
    const documentData = {
      [videoId]: videoEntry,
    };

    // Update the VIDEO_PROGRESS document with merge.
    // Deliberately the only write: a progress save must never touch users/{patientId}.
    // Writing applicationStatus there reopened closed/released applications on every
    // rewatch, and stamping lastUpdated re-sorted the console patient list every ~5s
    // during playback (CU-17tm466h648).
    await setDoc(videoProgressDocRef, documentData, { merge: true });
  } catch (error) {
    console.error('Error updating video progress:', error);
    Sentry.captureException(error);
    throw error;
  }
};

export function useUpdateVideoProgress() {
  return useMutation({
    mutationFn: (payload: UpdateVideoProgressPayload) => updateVideoProgress(payload),
  });
}
